import * as React from 'react'
import { cn } from '@/lib/utils'

type TypographyProps<T extends React.ElementType> = {
  as?: T
  children: React.ReactNode
  className?: string
} & React.ComponentPropsWithoutRef<T>

export function TypographyH1<T extends React.ElementType = 'h1'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'h1'
  return (
    <Component
      className={cn(
        'scroll-m-20 text-4xl font-extrabold tracking-tight text-balance',
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

export function TypographyH2<T extends React.ElementType = 'h2'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'h2'
  return (
    <Component
      className={cn(
        'mt-10 scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight transition-colors first:mt-0',
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

export function TypographyH3<T extends React.ElementType = 'h3'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'h3'
  return (
    <Component
      className={cn(
        'mt-8 scroll-m-20 text-2xl font-semibold tracking-tight',
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

export function TypographyH4<T extends React.ElementType = 'h4'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'h4'
  return (
    <Component
      className={cn('scroll-m-20 text-xl font-semibold tracking-tight', className)}
      {...props}
    >
      {children}
    </Component>
  )
}

export function TypographyP<T extends React.ElementType = 'p'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'p'
  return (
    <Component
      className={cn('leading-7 [&:not(:first-child)]:mt-6', className)}
      {...props}
    >
      {children}
    </Component>
  )
}

export function TypographyBlockquote<T extends React.ElementType = 'blockquote'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'blockquote'
  return (
    <Component
      className={cn('mt-6 border-l-2 pl-6 italic', className)}
      {...props}
    >
      {children}
    </Component>
  )
}

export function TypographyList<T extends React.ElementType = 'ul'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'ul'
  return (
    <Component
      className={cn('my-6 ml-6 list-disc [&>li]:mt-2', className)}
      {...props}
    >
      {children}
    </Component>
  )
}

export function TypographyInlineCode<T extends React.ElementType = 'code'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'code'
  return (
    <Component
      className={cn(
        'bg-muted relative rounded px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold',
        className
      )}
      {...props}
    >
      {children}
    </Component>
  )
}

export function TypographyLead<T extends React.ElementType = 'p'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'p'
  return (
    <Component
      className={cn('text-muted-foreground text-xl', className)}
      {...props}
    >
      {children}
    </Component>
  )
}

export function TypographyLarge<T extends React.ElementType = 'div'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'div'
  return (
    <Component className={cn('text-lg font-semibold', className)} {...props}>
      {children}
    </Component>
  )
}

export function TypographySmall<T extends React.ElementType = 'small'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'small'
  return (
    <Component
      className={cn('text-sm leading-none font-medium', className)}
      {...props}
    >
      {children}
    </Component>
  )
}

export function TypographyMuted<T extends React.ElementType = 'p'>({
  as,
  children,
  className,
  ...props
}: TypographyProps<T>) {
  const Component = as || 'p'
  return (
    <Component
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    >
      {children}
    </Component>
  )
}